import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import {
  articleService, nomenclatureService, VarianteView, NomenclatureLigneView,
} from '../../api/services';
import { Produit360 as Produit360Data, ComparateurFournisseur } from '../../types';
import { MoneyDisplay } from '../../components/common/MoneyDisplay';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { formatDate } from '../../utils/formatters';
import toast from 'react-hot-toast';

/**
 * Fiche produit 360° (TF-016) : performance commerciale, marge, stock et CO₂.
 */
export function Produit360() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<Produit360Data | null>(null);
  const [loading, setLoading] = useState(true);
  const [comp, setComp] = useState<ComparateurFournisseur | null>(null);
  const [loadingComp, setLoadingComp] = useState(false);
  // TF-076 — variantes + nomenclature
  const [variantes, setVariantes] = useState<VarianteView[]>([]);
  const [bom, setBom] = useState<NomenclatureLigneView[]>([]);
  const [articles, setArticles] = useState<any[]>([]);
  const [attributs, setAttributs] = useState('');
  const [composantId, setComposantId] = useState('');
  const [qteComposant, setQteComposant] = useState('');
  const [qteAssemblage, setQteAssemblage] = useState('');
  const [busy76, setBusy76] = useState(false);

  const load76 = React.useCallback(() => {
    if (!id) return;
    nomenclatureService.variantes(Number(id)).then(setVariantes).catch(() => {});
    nomenclatureService.nomenclature(Number(id)).then(setBom).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!id) return;
    articleService.getFiche360(Number(id)).then(setData).finally(() => setLoading(false));
    articleService.getAll({ size: 1000 }).then((p: any) => setArticles(p.content ?? p)).catch(() => {});
    load76();
  }, [id, load76]);

  const creerVariante = async () => {
    if (!attributs.trim()) { toast.error('Attributs requis (ex. « Taille: M »).'); return; }
    setBusy76(true);
    try {
      await nomenclatureService.createVariante(Number(id), { attributs: attributs.trim() });
      setAttributs('');
      toast.success('Variante créée.');
      load76();
    } catch (e: any) { toast.error(e?.response?.data?.message || 'Création impossible.'); }
    finally { setBusy76(false); }
  };

  const ajouterComposant = async () => {
    if (!composantId || !qteComposant) { toast.error('Composant et quantité requis.'); return; }
    setBusy76(true);
    try {
      await nomenclatureService.addComposant(Number(id), {
        composantId: Number(composantId), quantite: Number(qteComposant),
      });
      setComposantId(''); setQteComposant('');
      load76();
    } catch (e: any) { toast.error(e?.response?.data?.message || 'Ajout impossible.'); }
    finally { setBusy76(false); }
  };

  const lancerAssemblage = async () => {
    if (!qteAssemblage) { toast.error('Quantité à assembler requise.'); return; }
    setBusy76(true);
    try {
      const r = await nomenclatureService.assembler(Number(id), { quantite: Number(qteAssemblage) });
      toast.success(`${r.quantiteProduite} unité(s) assemblée(s) — coût ${r.coutUnitaireProduction.toFixed(3)} TND/u.`);
      setQteAssemblage('');
      articleService.getFiche360(Number(id)).then(setData);
      load76();
    } catch (e: any) { toast.error(e?.response?.data?.message || 'Assemblage impossible.'); }
    finally { setBusy76(false); }
  };

  const loadComparateur = async () => {
    if (!id) return;
    setLoadingComp(true);
    try {
      setComp(await articleService.getComparateurFournisseurs(Number(id)));
    } catch {
      toast.error('Comparateur indisponible');
    } finally {
      setLoadingComp(false);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (!data) return null;

  const kg = (v?: number | null) => (v == null ? '—' : `${v.toFixed(1)} kg`);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate(-1)} className="btn-outline p-2"><ArrowLeftIcon className="h-5 w-5" /></button>
          <div className="min-w-0">
            <h1 className="page-title truncate">{data.designation}</h1>
            <p className="page-subtitle">{data.reference || '—'}</p>
          </div>
        </div>
        <button onClick={() => navigate(`/articles/${data.articleId}/edit`)} className="btn-secondary">
          <PencilSquareIcon className="h-4 w-4 mr-1" /> Éditer
        </button>
      </div>

      <section>
        <h2 className="text-sm font-semibold text-gray-700 mb-2">Commercial</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Kpi label="Quantité vendue">{data.quantiteVendue}</Kpi>
          <Kpi label="CA HT"><MoneyDisplay amount={data.caHT} size="sm" /></Kpi>
          <Kpi label="Factures">{data.nbFactures}</Kpi>
          <Kpi label="Dernière vente">{data.derniereVente ? formatDate(data.derniereVente) : '—'}</Kpi>
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-gray-700 mb-2">Marge (CMUP)</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Kpi label="CMUP"><MoneyDisplay amount={data.cmup} size="sm" /></Kpi>
          <Kpi label="Coût HT"><MoneyDisplay amount={data.coutHT} size="sm" /></Kpi>
          <Kpi label="Marge"><MoneyDisplay amount={data.marge} size="sm" className="text-emerald-700" /></Kpi>
          <Kpi label="Taux de marge">{data.tauxMargePct == null ? '—' : `${data.tauxMargePct.toFixed(1)} %`}</Kpi>
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-gray-700 mb-2">Stock & carbone</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Kpi label="Stock actuel" accent={data.enRupture ? 'text-red-600' : undefined}>
            {data.stockActuel}{data.enRupture && ' ⚠'}
          </Kpi>
          <Kpi label="Stock minimum">{data.stockMinimum ?? '—'}</Kpi>
          <Kpi label="CO₂ vendu">{kg(data.co2VenduKg)}</Kpi>
          <Kpi label="CO₂ en stock">{kg(data.co2StockKg)}</Kpi>
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold text-gray-700">Comparateur fournisseurs (prix / délai / qualité / CO₂)</h2>
          {!comp && (
            <button onClick={loadComparateur} className="btn-outline" disabled={loadingComp}>
              {loadingComp ? 'Chargement…' : 'Comparer les fournisseurs'}
            </button>
          )}
        </div>
        {comp && (
          <div className="card overflow-x-auto">
            {comp.offres.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">Aucun fournisseur associé à cet article.</p>
            ) : (
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50"><tr className="text-left text-xs font-medium text-gray-500 uppercase">
                  <th className="px-3 py-2">Fournisseur</th>
                  <th className="px-3 py-2 text-right">Prix</th>
                  <th className="px-3 py-2 text-right">Délai (j)</th>
                  <th className="px-3 py-2 text-right">Qualité</th>
                  <th className="px-3 py-2 text-right">CO₂ (kg/TND)</th>
                  <th className="px-3 py-2 text-right">Score global</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {comp.offres.map((o, i) => (
                    <tr key={o.fournisseurId} className={i === 0 ? 'bg-emerald-50' : ''}>
                      <td className="px-3 py-2 font-medium">
                        {i === 0 && '🏆 '}{o.fournisseurRaisonSociale}
                        {o.prefere && <span className="ml-1 text-xs text-accent-600">préféré</span>}
                      </td>
                      <td className="px-3 py-2 text-right">{o.prixAchat == null ? '—' : <MoneyDisplay amount={o.prixAchat} size="sm" />}</td>
                      <td className="px-3 py-2 text-right">{o.delaiJours ?? '—'}</td>
                      <td className="px-3 py-2 text-right">{o.noteQualite == null ? '—' : `${o.noteQualite}/5`}</td>
                      <td className="px-3 py-2 text-right">{o.intensiteCo2 == null ? '—' : o.intensiteCo2.toFixed(3)}</td>
                      <td className="px-3 py-2 text-right font-bold">{o.scoreGlobal}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </section>

      {/* TF-076 — variantes / déclinaisons */}
      <section>
        <h2 className="text-sm font-semibold text-gray-700 mb-2">Variantes / déclinaisons</h2>
        <div className="card space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-56">
              <label className="block text-xs text-gray-600 mb-1">Attributs de la nouvelle variante</label>
              <input value={attributs} onChange={(e) => setAttributs(e.target.value)}
                     placeholder="Taille: M · Couleur: rouge"
                     className="w-full rounded-lg border border-gray-200 px-3 py-2" />
            </div>
            <button onClick={creerVariante} disabled={busy76} className="btn-secondary">Créer la variante</button>
          </div>
          {variantes.length > 0 && (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50"><tr className="text-left text-xs font-medium text-gray-500 uppercase">
                <th className="px-3 py-2">Référence</th><th className="px-3 py-2">Attributs</th>
                <th className="px-3 py-2 text-right">Prix HT</th><th className="px-3 py-2 text-right">Stock</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {variantes.map((v) => (
                  <tr key={v.id} className="cursor-pointer hover:bg-gray-50"
                      onClick={() => navigate(`/articles/${v.id}/360`)}>
                    <td className="px-3 py-2 font-mono text-xs">{v.reference}</td>
                    <td className="px-3 py-2">{v.attributs}</td>
                    <td className="px-3 py-2 text-right"><MoneyDisplay amount={v.prixUnitaire} size="sm" /></td>
                    <td className="px-3 py-2 text-right">{v.stockActuel}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* TF-076 — nomenclature / BOM + assemblage */}
      <section>
        <h2 className="text-sm font-semibold text-gray-700 mb-2">Nomenclature (BOM) & assemblage</h2>
        <div className="card space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-56">
              <label className="block text-xs text-gray-600 mb-1">Composant</label>
              <select value={composantId} onChange={(e) => setComposantId(e.target.value)}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2">
                <option value="">— Choisir —</option>
                {articles.filter((a: any) => a.id !== Number(id)).map((a: any) => (
                  <option key={a.id} value={a.id}>{a.reference} — {a.designation}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Quantité / unité</label>
              <input type="number" step="0.001" value={qteComposant}
                     onChange={(e) => setQteComposant(e.target.value)}
                     className="w-28 rounded-lg border border-gray-200 px-3 py-2" />
            </div>
            <button onClick={ajouterComposant} disabled={busy76} className="btn-secondary">Ajouter</button>
          </div>
          {bom.length > 0 && (
            <>
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50"><tr className="text-left text-xs font-medium text-gray-500 uppercase">
                  <th className="px-3 py-2">Composant</th>
                  <th className="px-3 py-2 text-right">Quantité / unité</th>
                  <th className="px-3 py-2 text-right">CMUP composant</th>
                  <th className="px-3 py-2 text-right"></th>
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {bom.map((l) => (
                    <tr key={l.id}>
                      <td className="px-3 py-2">{l.composantReference} — {l.composantDesignation}</td>
                      <td className="px-3 py-2 text-right">{l.quantite}</td>
                      <td className="px-3 py-2 text-right"><MoneyDisplay amount={l.cmupComposant} size="sm" /></td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => nomenclatureService.removeComposant(Number(id), l.id).then(load76)}
                                className="text-red-600 hover:underline text-xs">Retirer</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex items-end gap-3 border-t border-gray-100 pt-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Quantité à assembler</label>
                  <input type="number" step="0.001" value={qteAssemblage}
                         onChange={(e) => setQteAssemblage(e.target.value)}
                         className="w-32 rounded-lg border border-gray-200 px-3 py-2" />
                </div>
                <button onClick={lancerAssemblage} disabled={busy76} className="btn-primary">
                  {busy76 ? 'Assemblage…' : 'Assembler'}
                </button>
                <p className="text-xs text-gray-400 self-center">
                  Consomme les composants (sorties au CMUP) et produit le composé au coût des composants.
                </p>
              </div>
            </>
          )}
          {bom.length === 0 && (
            <p className="text-xs text-gray-400">Aucun composant — ajoutez-en pour définir la nomenclature.</p>
          )}
        </div>
      </section>
    </div>
  );
}

const Kpi: React.FC<{ label: string; accent?: string; children: React.ReactNode }> = ({ label, accent, children }) => (
  <div className="card">
    <p className="text-xs uppercase tracking-wide text-gray-500 font-medium">{label}</p>
    <div className={`mt-1 text-lg font-semibold ${accent ?? 'text-gray-800'}`}>{children}</div>
  </div>
);

export default Produit360;
